import { CommandType, RegType, type FullAirHandlerUnit } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { cn } from "@eisox/design-system";

import Warning from "~/assets/boilerroomWarning.svg?react";
import { Tooltip } from "~/components/tooltip";
import { m } from "~/paraglide/messages";

import { checkInconsistencies } from "../helpers";
import { useAirHandlerUnitContext } from "../providers/air-handler-unit.provider";

const NAME = "Inconsistency";

interface InconsistencyProps {
  property: Paths<FullAirHandlerUnit>;
}

const Inconsistency: React.FC<InconsistencyProps> = ({ property }) => {
  const { airHandlerUnit, date } = useAirHandlerUnitContext(NAME);

  const regulatedAirHandlerUnits = airHandlerUnit.filter(ahu => ahu.regType !== RegType.NONE);

  const primaryAirHandlerUnit = airHandlerUnit[0];
  if (!primaryAirHandlerUnit) return null;

  const inconsistencies = checkInconsistencies(
    // Reg type and command type are required to be set in type air handler unit. Value does not matter here.
    {
      ...primaryAirHandlerUnit,
      regType: RegType.RETURN_SETPOINT,
      commandType: CommandType.ALL_OR_NOTHING,
      lastBmsDataReport: date.toISOString(),
    },
    regulatedAirHandlerUnits,
    property,
  );

  if (!inconsistencies?.inconsistency) return null;

  return (
    <Tooltip
      content={
        <div className="flex flex-col gap-[10px]">
          <p>{m.data_not_synchronized()}</p>
          {Array.from(inconsistencies.inconsistency).map(
            ([key, { inconsistency, value, date }]) =>
              (typeof value === "string" ||
                typeof value === "number" ||
                typeof value === "boolean" ||
                typeof value === "undefined") && (
                <p
                  key={key}
                  className={cn(
                    "flex items-center gap-[5px] before:h-[10px] before:w-[10px] before:rounded-full before:bg-primary-green before:content-['']",
                    inconsistency && "before:bg-primary-red",
                  )}
                >
                  {key}: {value ?? "-"}{" "}
                  <span className="text-sm italic">{date ? dayjs(date).format("LTS - L") : "-"}</span>
                </p>
              ),
          )}
        </div>
      }
    >
      <Warning />
    </Tooltip>
  );
};

Inconsistency.displayName = NAME;

export { Inconsistency };
export type { InconsistencyProps };
